// src/utils/api_admin.js
//
// Llamadas HTTP para el dashboard de Armando/Ceci.
// Usa la misma instancia `api` de api_backend.js pero con
// token del admin (localStorage: 'admin_token').
//
// Patrón idéntico al de api_backend.js — mismo interceptor,
// misma gestión de errores.

import axios from 'axios'
import router from '@/router'
import { useAdminStore } from '@/store/adminStore'
import Swal from 'sweetalert2'

const API_KEY    = process.env.VUE_APP_API_KEY
const apiBaseUrl = process.env.VUE_APP_API

export const apiAdmin = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept':        'application/json',
  },
})

apiAdmin.interceptors.request.use(config => {
  if (API_KEY) config.headers['x-api-key'] = API_KEY
  const token = localStorage.getItem('admin_token')
  if (token) config.headers['Authorization'] = `Bearer ${token}`
  return config
}, error => Promise.reject(error))

// ✅ FIX: mismo problema que en api_dashboard.js — token vencido a mitad de
// sesión se quedaba atorado dentro del dashboard, tronando 401 en cada
// request sin sacar a la persona al login.
//
// IMPORTANTE: se excluye 'admin/login/' — un 401 ahí es contraseña
// incorrecta, no una sesión que expiró. Mostrar "Sesión expirada" en la
// pantalla de login sería confuso, porque nunca hubo sesión.
let sesionExpiradaEnCurso = false

apiAdmin.interceptors.response.use(
  response => response,
  async error => {
    const status  = error.response?.status
    const esLogin = error.config?.url?.includes('admin/login/')

    if (status === 401 && !esLogin && !sesionExpiradaEnCurso) {
      sesionExpiradaEnCurso = true

      const store = useAdminStore()
      store.logout()

      await Swal.fire({
        icon: 'info',
        title: 'Sesión expirada',
        text: 'Tu sesión ha vencido. Inicia sesión de nuevo para continuar.',
        confirmButtonColor: '#f3bc2c',
        confirmButtonText: 'Entendido',
      })

      sesionExpiradaEnCurso = false

      if (router.currentRoute.value.name !== 'DashboardLogin') {
        router.push({ name: 'DashboardLogin' })
      }
    }

    return Promise.reject(error)
  }
)


// ─────────────────────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────────────────────
export const loginAdmin = async ({ email, password }) => {
  const res = await apiAdmin.post('admin/login/', { email, password })
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// EVENTO — RESUMEN EN VIVO
// ─────────────────────────────────────────────────────────────────────────────
export const getResumenEvento = async (torneoId) => {
  const res = await apiAdmin.get('admin/evento/resumen/', {
    params: { torneo_id: torneoId },
  })
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// BRACKETS
// ─────────────────────────────────────────────────────────────────────────────
export const getBrackets = async (torneoId) => {
  const res = await apiAdmin.get('brackets/', {
    params: { torneo: torneoId },
  })
  return res.data?.results ?? res.data ?? []
}

export const generarBracket = async (bracketId) => {
  const res = await apiAdmin.post(`admin/brackets/${bracketId}/generar/`)
  return res.data
}

export const fusionarBrackets = async ({ bracketOrigenId, bracketDestinoId }) => {
  const res = await apiAdmin.post('admin/brackets/fusionar/', {
    bracket_origen_id:  bracketOrigenId,
    bracket_destino_id: bracketDestinoId,
  })
  return res.data
}

export const getCandidatosFusionAdultos = async (torneoId) => {
  const res = await apiAdmin.get('admin/brackets/candidatos-fusion/', {
    params: { torneo_id: torneoId },
  })
  return res.data
}

export const getCandidatosFusionJuveniles = async (bracketId, torneoId, filtro = null) => {
  const params = { torneo_id: torneoId }
  if (filtro) params.filtro = filtro
  const res = await apiAdmin.get(
    `admin/brackets/${bracketId}/candidatos-fusion-juvenil/`,
    { params },
  )
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// PELEAS — EMERGENCIA
// ─────────────────────────────────────────────────────────────────────────────
export const resolverPelea = async (peleaId, { ganador, tipo }) => {
  const res = await apiAdmin.post(`admin/peleas/${peleaId}/resolver/`, {
    ganador,
    tipo,
  })
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// PELEAS — LISTADO POR ESTADO (para pantalla de emergencia)
// ─────────────────────────────────────────────────────────────────────────────
export const getPeleasUrgentes = async (torneoId) => {
  const res = await apiAdmin.get('peleas/', {
    params: {
      tatami__torneo: torneoId,
      estado:         'tiempo_agotado',
    },
  })
  return res.data?.results ?? res.data ?? []
}


// ─────────────────────────────────────────────────────────────────────────────
// AUTO-GENERAR BRACKETS
// ─────────────────────────────────────────────────────────────────────────────
export const autoGenerarBrackets = async (torneoId) => {
  const res = await apiAdmin.post('admin/brackets/auto-generar/', null, {
    params: { torneo_id: torneoId },
  })
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// TATAMIS — REBALANCEO MANUAL
// ─────────────────────────────────────────────────────────────────────────────
export const rebalancearTatamis = async (torneoId) => {
  const res = await apiAdmin.post('admin/tatamis/rebalancear/', null, {
    params: { torneo_id: torneoId },
  })
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// PELEAS DE UN BRACKET
// ─────────────────────────────────────────────────────────────────────────────
export const getPeleasBracket = async (bracketId) => {
  const res = await apiAdmin.get('peleas/', {
    params: { bracket: bracketId },
  })
  return res.data?.results ?? res.data ?? []
}